// Login com Google ou Microsoft no desktop (P0.6, Segurança item 7; AC-085, AC-090): fluxo PKCE
// do Supabase, abre o navegador do SISTEMA e volta para o app pelo link beedoit://auth/callback.
import { AUTH_CALLBACK_URL, OAUTH_SCOPES, type OAuthProvider } from '../data/auth';

export interface PkceAuthClient {
  signInWithOAuth(args: {
    provider: OAuthProvider;
    options: { redirectTo: string; skipBrowserRedirect: boolean; scopes?: string };
  }): Promise<{ data: { url: string | null }; error: unknown }>;
  exchangeCodeForSession(code: string): Promise<{ error: unknown }>;
}

export interface DesktopOAuthDeps {
  auth: PkceAuthClient;
  openExternal(url: string): Promise<void>;
}

/** Gera a URL do provedor com o desafio PKCE e abre no navegador do sistema. */
export async function desktopOAuthSignIn({ auth, openExternal }: DesktopOAuthDeps, provider: OAuthProvider): Promise<void> {
  const scopes = OAUTH_SCOPES[provider];
  const { data, error } = await auth.signInWithOAuth({
    provider,
    options: { redirectTo: AUTH_CALLBACK_URL, skipBrowserRedirect: true, ...(scopes ? { scopes } : {}) },
  });
  if (error || !data.url) throw error ?? new Error('sem url de login');
  await openExternal(data.url);
}

/** Lê o código do retorno beedoit://auth/callback?code=… (null se não for o nosso link). */
export function parseAuthCallback(url: string): { code: string } | { error: string } | null {
  if (!url.startsWith(AUTH_CALLBACK_URL)) return null;
  const query = url.slice(url.indexOf('?') + 1);
  const params = new URLSearchParams(url.includes('?') ? query : '');
  const code = params.get('code');
  if (code) return { code };
  return { error: params.get('error_description') ?? params.get('error') ?? 'unknown' };
}

/** Troca o código pela sessão (o verificador PKCE ficou guardado no cofre do sistema). */
export async function handleAuthCallback(url: string, auth: PkceAuthClient): Promise<boolean> {
  const parsed = parseAuthCallback(url);
  if (!parsed || !('code' in parsed)) return false;
  const { error } = await auth.exchangeCodeForSession(parsed.code);
  return !error;
}
